import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api, { setTokenGetter } from '../services/api';
import { useI18n } from '../i18n';

const MeContext = createContext({ me: null, loading: true, error: null, reload: () => {} });

// Diagnóstico: qué identidad devuelve Auth0 y qué viaja realmente en el token.
function logAuthDiagnostics(user, token, me) {
  console.group('[auth] identidad de Auth0');
  console.log('user (perfil del ID token):', user);
  console.log('  email        :', user?.email ?? '(ausente)');
  console.log('  sub          :', user?.sub ?? '(ausente)');
  console.log('  email_verified:', user?.email_verified);

  if (token) {
    try {
      const [, body] = token.split('.');
      const claims = JSON.parse(
        atob(body.replace(/-/g, '+').replace(/_/g, '/'))
      );
      console.log('access token — claims:', claims);
      console.log('  sub          :', claims.sub);
      console.log('  aud          :', claims.aud);
      const emailClaims = Object.entries(claims).filter(([k]) => /email/i.test(k));
      console.log('  claims con "email":',
        emailClaims.length ? Object.fromEntries(emailClaims) : '(NINGUNO — falta la Action de Auth0)');
    } catch (e) {
      console.warn('  no se pudo decodificar el access token:', e.message);
    }
  } else {
    console.warn('  no se obtuvo access token');
  }

  console.log('respuesta de /api/me:', me);
  console.groupEnd();
}

export function MeProvider({ children }) {
  const { isAuthenticated, isLoading, getAccessTokenSilently, user } = useAuth0();
  const { t } = useI18n();
  // El error se guarda como clave, no como texto: así se retraduce si el
  // usuario cambia de idioma sin recargar.
  const [state, setState] = useState({ me: null, loading: true, errorKey: null });

  const fetchMe = useCallback(async (profile) => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const res = await api.get('/api/me');

      let token = null;
      try {
        token = await getAccessTokenSilently({
          authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
        });
      } catch { /* el diagnóstico nunca debe romper el arranque */ }
      logAuthDiagnostics(profile, token, res.data);

      setState({ me: res.data, loading: false, errorKey: null });
    } catch (err) {
      console.error('[auth] /api/me falló:', err.response?.status, err.response?.data || err.message);
      // 401/403 aquí no significan "sin acceso": el backend responde 200 con
      // access_granted:false para eso. Son fallos reales y hay que decirlo,
      // en vez de mostrar la pantalla de bloqueo por un problema de red.
      setState({
        me: null,
        loading: false,
        errorKey: err.response?.status === 401 ? 'meErrors.session' : 'meErrors.server',
      });
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      setState({ me: null, loading: false, errorKey: null });
      return;
    }

    // El proveedor de token se registra aquí, justo antes de la primera
    // llamada, para no depender del orden de los efectos de otros componentes.
    setTokenGetter(() =>
      getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
      })
    );

    fetchMe(user);
  }, [isAuthenticated, isLoading, getAccessTokenSilently, fetchMe, user]);

  return (
    <MeContext.Provider
      value={{ ...state, error: state.errorKey ? t(state.errorKey) : null, reload: fetchMe }}
    >
      {children}
    </MeContext.Provider>
  );
}

export const useMe = () => useContext(MeContext);
export const useCan = (permission) => {
  const { me } = useMe();
  return Boolean(me?.permissions?.includes(permission));
};
